import { useEffect, useState, useCallback } from "react";
import { MainLayout } from "../components/layout/MainLayout";
import { Header } from "../components/layout/Header";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Card, CardContent } from "../components/ui/card";
import { Calendar } from "../components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../components/ui/sheet";
import { Separator } from "../components/ui/separator";
import { Badge } from "../components/ui/badge";
import {
  Calendar as CalendarIcon,
  FileDown,
  Search,
  Receipt,
  DollarSign,
  FileText,
  Eye,
  ShoppingCart,
  User,
  CreditCard,
  Package,
  Percent,
  TrendingUp,
} from "lucide-react";
import { format } from "date-fns";
import { cn } from "../lib/utils";
import { useToast } from "../components/ui/use-toast";
import {
  get_invoice_sales_report,
  server_post_data,
} from "../ServiceConnection/serviceconnection";
import * as FileSaver from "file-saver";
import XLSX from "sheetjs-style";

export default function InvoiceWiseReport() {
  const { toast } = useToast();

  // Date states
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
  const [startDate, setStartDate] = useState(sevenDaysAgo);
  const [endDate, setEndDate] = useState(new Date());
  const [filterOption, setFilterOption] = useState("");
  const [paymentFilter, setPaymentFilter] = useState("all");

  // Data states
  const [showLoaderAdmin, setShowLoaderAdmin] = useState(false);
  const [invoiceData, setInvoiceData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const entriesPerPage = 10;

  // Sidebar state
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  // Stats
  const [stats, setStats] = useState({
    totalInvoices: 0,
    totalAmount: 0,
    totalTax: 0,
    totalDiscount: 0,
  });

  // Fetch data
  const fetchReportData = useCallback(
    async (start_date, end_date, flag = "1") => {
      // Handle date filtering
      if (flag === "2") {
        if (filterOption === "today") {
          const today = new Date();
          start_date = new Date(today.setHours(0, 0, 0, 0));
          end_date = new Date(today.setHours(23, 59, 59, 999));
        } else if (filterOption === "this_week") {
          const today = new Date();
          const firstDayOfWeek = new Date(
            today.setDate(today.getDate() - today.getDay()),
          );
          start_date = new Date(firstDayOfWeek.setHours(0, 0, 0, 0));
          end_date = new Date(today.setHours(23, 59, 59, 999));
        } else if (filterOption === "this_month") {
          const today = new Date();
          start_date = new Date(
            today.getFullYear(),
            today.getMonth(),
            1,
            0,
            0,
            0,
          );
          end_date = new Date(
            today.getFullYear(),
            today.getMonth() + 1,
            0,
            23,
            59,
            59,
          );
        } else if (filterOption === "this_year") {
          const today = new Date();
          start_date = new Date(today.getFullYear(), 0, 1, 0, 0, 0);
          end_date = new Date(today.getFullYear(), 11, 31, 23, 59, 59);
        } else if (filterOption === "custom") {
          if (!startDate || !endDate) {
            toast({
              title: "Validation Error",
              description: "Please select both start and end dates.",
              variant: "destructive",
            });
            return;
          }
        }

        if (start_date > end_date) {
          toast({
            title: "Validation Error",
            description: "Start date cannot be greater than end date.",
            variant: "destructive",
          });
          return;
        }
      }

      setShowLoaderAdmin(true);
      const fd = new FormData();
      fd.append("start_date", start_date.toISOString().split("T")[0]);
      fd.append("end_date", end_date.toISOString().split("T")[0]);
      fd.append("flag", flag);
      fd.append("payment_type", paymentFilter);

      try {
        const response = await server_post_data(get_invoice_sales_report, fd);

        if (response.data.error) {
          toast({
            title: "Error",
            description: response.data.message,
            variant: "destructive",
          });
        } else {
          const data = response.data.message || [];
          setInvoiceData(data);

          // Calculate stats
          const totalAmount = data.reduce(
            (sum, item) => sum + parseFloat(item.total_amount || 0),
            0,
          );
          const totalTax = data.reduce(
            (sum, item) => sum + parseFloat(item.tax_amount || 0),
            0,
          );
          const totalDiscount = data.reduce(
            (sum, item) => sum + parseFloat(item.discount_amount || 0),
            0,
          );

          setStats({
            totalInvoices: data.length,
            totalAmount: totalAmount,
            totalTax: totalTax,
            totalDiscount: totalDiscount,
          });
        }
      } catch (error) {
        console.error("Error fetching report:", error);
        toast({
          title: "Error",
          description: "Failed to load report data",
          variant: "destructive",
        });
      } finally {
        setShowLoaderAdmin(false);
      }
    },
    [filterOption, paymentFilter, startDate, endDate, toast],
  );

  // Handle search
  const handleSearch = useCallback(() => {
    setCurrentPage(1);
    fetchReportData(startDate, endDate, "2");
  }, [startDate, endDate, fetchReportData]);

  // Export to Excel with full details
  const exportToExcel = useCallback(() => {
    if (invoiceData.length === 0) {
      toast({
        title: "No Data",
        description: "No data available to export",
        variant: "destructive",
      });
      return;
    }

    // Prepare detailed data for Excel export
    const exportData = [];

    invoiceData.forEach((invoice) => {
      // Add items for each invoice
      if (invoice.items && invoice.items.length > 0) {
        invoice.items.forEach((item, index) => {
          exportData.push({
            "Invoice No": index === 0 ? invoice.invoice_no : "",
            "Invoice Date": index === 0 ? invoice.invoice_date : "",
            "Customer Name": index === 0 ? invoice.customer_name : "",
            "Customer Phone": index === 0 ? invoice.customer_phone : "",
            "Customer Email": index === 0 ? invoice.customer_email : "",
            "Item Name": item.item_name,
            Category: item.category,
            Quantity: item.quantity,
            "Unit Price": item.unit_price,
            "Item Discount": item.discount,
            "Item Tax": item.tax,
            "Item Total": item.item_total,
            Subtotal: index === 0 ? invoice.subtotal : "",
            "Total Discount": index === 0 ? invoice.discount_amount : "",
            "Total Tax": index === 0 ? invoice.tax_amount : "",
            "Delivery Charge": index === 0 ? invoice.delivery_charge : "",
            "Grand Total": index === 0 ? invoice.total_amount : "",
            "Payment Method": index === 0 ? invoice.payment_method : "",
            "Payment Status": index === 0 ? invoice.payment_status : "",
          });
        });
      } else {
        // If no items, add invoice summary
        exportData.push({
          "Invoice No": invoice.invoice_no,
          "Invoice Date": invoice.invoice_date,
          "Customer Name": invoice.customer_name,
          "Customer Phone": invoice.customer_phone,
          "Customer Email": invoice.customer_email,
          "Item Name": "",
          Category: "",
          Quantity: "",
          "Unit Price": "",
          "Item Discount": "",
          "Item Tax": "",
          "Item Total": "",
          Subtotal: invoice.subtotal,
          "Total Discount": invoice.discount_amount,
          "Total Tax": invoice.tax_amount,
          "Delivery Charge": invoice.delivery_charge,
          "Grand Total": invoice.total_amount,
          "Payment Method": invoice.payment_method,
          "Payment Status": invoice.payment_status,
        });
      }
    });

    const fileType =
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";

    const ws = XLSX.utils.json_to_sheet(exportData);

    // Set column widths
    ws["!cols"] = [
      { wch: 12 }, // Invoice No
      { wch: 12 }, // Invoice Date
      { wch: 20 }, // Customer Name
      { wch: 15 }, // Customer Phone
      { wch: 25 }, // Customer Email
      { wch: 30 }, // Item Name
      { wch: 15 }, // Category
      { wch: 10 }, // Quantity
      { wch: 12 }, // Unit Price
      { wch: 12 }, // Item Discount
      { wch: 10 }, // Item Tax
      { wch: 12 }, // Item Total
      { wch: 12 }, // Subtotal
      { wch: 12 }, // Total Discount
      { wch: 12 }, // Total Tax
      { wch: 12 }, // Delivery Charge
      { wch: 12 }, // Grand Total
      { wch: 15 }, // Payment Method
      { wch: 15 }, // Payment Status
    ];

    // Style header row
    const range = XLSX.utils.decode_range(ws["!ref"]);
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const address = XLSX.utils.encode_col(C) + "1";
      if (!ws[address]) continue;
      ws[address].s = {
        font: { bold: true, color: { rgb: "FFFFFF" } },
        fill: { fgColor: { rgb: "017268" } },
        alignment: { horizontal: "center", vertical: "center" },
      };
    }

    const wb = {
      Sheets: { "Invoice Report": ws },
      SheetNames: ["Invoice Report"],
    };
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const data = new Blob([excelBuffer], { type: fileType });

    const fileName = `Invoice_Report_${format(new Date(), "yyyy_MM_dd_HH_mm_ss")}.xlsx`;
    FileSaver.saveAs(data, fileName);

    toast({
      title: "Success",
      description: "Report exported successfully with full details",
    });
  }, [invoiceData, toast]);

  // Pagination
  const totalPages = Math.ceil(invoiceData.length / entriesPerPage);
  const currentData = invoiceData.slice(
    (currentPage - 1) * entriesPerPage,
    currentPage * entriesPerPage,
  );

  const handlePageChange = (pageNumber) => {
    if (pageNumber < 1 || pageNumber > totalPages) return;
    setCurrentPage(pageNumber);
  };


  // Handle view invoice details
  const handleViewInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setIsSidebarOpen(true);
  };

  // Get payment status badge color
  const getPaymentStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "paid":
        return "bg-green-500/10 text-green-700 border-green-500/20";
      case "pending":
        return "bg-yellow-500/10 text-yellow-700 border-yellow-500/20";
      case "failed":
        return "bg-red-500/10 text-red-700 border-red-500/20";
      default:
        return "bg-gray-500/10 text-gray-700 border-gray-500/20";
    }
  };

  return (
    <MainLayout>
      <Header
        title="Invoice-Wise Report"
        subtitle="View and analyze invoice details with customer orders"
      />

      <div className="p-6 space-y-6">
        {/* Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Receipt className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {stats.totalInvoices}
                </p>
                <p className="text-sm text-muted-foreground">Total Invoices</p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-success" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  ₹{stats.totalAmount.toFixed(2)}
                </p>
                <p className="text-sm text-muted-foreground">Total Amount</p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  ₹{stats.totalTax.toFixed(2)}
                </p>
                <p className="text-sm text-muted-foreground">Total Tax</p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-orange-500/10 flex items-center justify-center">
                <Percent className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  ₹{stats.totalDiscount.toFixed(2)}
                </p>
                <p className="text-sm text-muted-foreground">Total Discount</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters Card */}
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              {/* Filter Dropdown */}
              <div className="space-y-2">
                <Label>Date Filter</Label>
                <Select value={filterOption} onValueChange={setFilterOption}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select Option" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="today">Today</SelectItem>
                    <SelectItem value="this_week">This Week</SelectItem>
                    <SelectItem value="this_month">This Month</SelectItem>
                    <SelectItem value="this_year">This Year</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Payment Filter */}
              <div className="space-y-2">
                <Label>Payment Method</Label>
                <Select value={paymentFilter} onValueChange={setPaymentFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Methods" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Methods</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="card">Card</SelectItem>
                    <SelectItem value="upi">UPI</SelectItem>
                    <SelectItem value="wallet">Wallet</SelectItem>
                    <SelectItem value="online">Online</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Start Date */}
              {filterOption === "custom" && (
                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !startDate && "text-muted-foreground",
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {startDate ? (
                          format(startDate, "dd MMM yyyy")
                        ) : (
                          <span>Pick a date</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={startDate}
                        onSelect={setStartDate}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              )}

              {/* End Date */}
              {filterOption === "custom" && (
                <div className="space-y-2">
                  <Label>End Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !endDate && "text-muted-foreground",
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {endDate ? (
                          format(endDate, "dd MMM yyyy")
                        ) : (
                          <span>Pick a date</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={endDate}
                        onSelect={setEndDate}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              )}

              {/* Action Buttons */}
              <div
                className={cn(
                  "flex items-end gap-2",
                  filterOption !== "custom" && "md:col-span-3",
                )}
              >
                <Button
                  onClick={handleSearch}
                  className="gap-2"
                  disabled={showLoaderAdmin}
                >
                  <Search className="w-4 h-4" />
                  Search
                </Button>
                <Button
                  variant="outline"
                  onClick={exportToExcel}
                  className="gap-2"
                  disabled={invoiceData.length === 0}
                >
                  <FileDown className="w-4 h-4" />
                  Export Full Details
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Table Card */}
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-muted-foreground">
                  Invoice No
                </TableHead>
                <TableHead className="text-muted-foreground">Date</TableHead>
                <TableHead className="text-muted-foreground">
                  Customer Name
                </TableHead>
                <TableHead className="text-muted-foreground text-right">
                  Total Amount
                </TableHead>
                <TableHead className="text-muted-foreground text-center">
                  Payment Method
                </TableHead>
                <TableHead className="text-muted-foreground text-center">
                  Status
                </TableHead>
                <TableHead className="text-muted-foreground text-center">
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {showLoaderAdmin ? (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    className="text-center text-muted-foreground"
                  >
                    Loading...
                  </TableCell>
                </TableRow>
              ) : currentData.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    className="text-center text-muted-foreground"
                  >
                    No data found
                  </TableCell>
                </TableRow>
              ) : (
                currentData.map((row, index) => (
                  <TableRow key={index} className="border-border">
                    <TableCell className="font-medium">
                      {row.invoice_no}
                    </TableCell>
                    <TableCell>{row.invoice_date}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-muted-foreground" />
                        {row.customer_name || "Walk-in Customer"}
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-semibold text-primary">
                      ₹{parseFloat(row.total_amount || 0).toFixed(2)}
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        <CreditCard className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm capitalize">
                          {row.payment_method || "N/A"}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge
                        variant="outline"
                        className={cn(
                          "capitalize",
                          getPaymentStatusColor(row.payment_status),
                        )}
                      >
                        {row.payment_status || "Pending"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="gap-2"
                        onClick={() => handleViewInvoice(row)}
                      >
                        <Eye className="w-4 h-4" />
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-border">
              <div className="text-sm text-muted-foreground">
                Showing {(currentPage - 1) * entriesPerPage + 1} to{" "}
                {Math.min(currentPage * entriesPerPage, invoiceData.length)} of{" "}
                {invoiceData.length} entries
              </div>
              <div className="flex gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                >
                  First
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                {[...Array(totalPages)].map((_, index) => {
                  const pageNumber = index + 1;
                  if (
                    pageNumber === 1 ||
                    pageNumber === totalPages ||
                    (pageNumber >= currentPage - 1 &&
                      pageNumber <= currentPage + 1)
                  ) {
                    return (
                      <Button
                        key={pageNumber}
                        variant={
                          pageNumber === currentPage ? "default" : "outline"
                        }
                        size="sm"
                        onClick={() => handlePageChange(pageNumber)}
                      >
                        {pageNumber}
                      </Button>
                    );
                  } else if (
                    pageNumber === currentPage - 2 ||
                    pageNumber === currentPage + 2
                  ) {
                    return <span key={pageNumber}>...</span>;
                  }
                  return null;
                })}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                >
                  Last
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Invoice Details Sidebar */}
      <Sheet open={isSidebarOpen} onOpenChange={setIsSidebarOpen}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Invoice Details</SheetTitle>
          </SheetHeader>

          {selectedInvoice && (
            <div className="mt-6 space-y-6">
              {/* Invoice Header */}
              <Card>
                <CardContent className="pt-6 space-y-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm text-muted-foreground">
                        Invoice Number
                      </p>
                      <p className="text-xl font-bold">
                        {selectedInvoice.invoice_no}
                      </p>
                    </div>
                    <Badge
                      variant="outline"
                      className={cn(
                        "capitalize",
                        getPaymentStatusColor(selectedInvoice.payment_status),
                      )}
                    >
                      {selectedInvoice.payment_status || "Pending"}
                    </Badge>
                  </div>
                  <Separator />
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Date</p>
                      <p className="font-medium">
                        {selectedInvoice.invoice_date}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Time</p>
                      <p className="font-medium">
                        {selectedInvoice.invoice_time || "N/A"}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Customer Details */}
              <div>
                <h3 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  CUSTOMER DETAILS
                </h3>
                <Card>
                  <CardContent className="pt-6 space-y-2.5">
                    <DetailRow
                      label="Name"
                      value={
                        selectedInvoice.customer_name || "Walk-in Customer"
                      }
                    />
                    <DetailRow
                      label="Phone"
                      value={selectedInvoice.customer_phone || "N/A"}
                    />
                    <DetailRow
                      label="Email"
                      value={selectedInvoice.customer_email || "N/A"}
                    />
                    <DetailRow
                      label="Address"
                      value={selectedInvoice.customer_address || "N/A"}
                    />
                  </CardContent>
                </Card>
              </div>

              {/* Order Items */}
              <div>
                <h3 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
                  <Package className="w-4 h-4" />
                  ORDER ITEMS
                </h3>
                <Card>
                  <CardContent className="pt-6">
                    <div className="space-y-4">
                      {selectedInvoice.items &&
                      selectedInvoice.items.length > 0 ? (
                        selectedInvoice.items.map((item, index) => (
                          <div
                            key={index}
                            className="flex justify-between items-start pb-4 border-b last:border-0 last:pb-0"
                          >
                            <div className="flex-1">
                              <p className="font-medium">{item.item_name}</p>
                              <p className="text-sm text-muted-foreground">
                                {item.category}
                              </p>
                              <p className="text-sm text-muted-foreground mt-1">
                                Qty: {item.quantity} × ₹{item.unit_price}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="font-semibold">
                                ₹{parseFloat(item.item_total || 0).toFixed(2)}
                              </p>
                              {item.discount > 0 && (
                                <p className="text-sm text-muted-foreground">
                                  Disc: -₹{item.discount}
                                </p>
                              )}
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-center text-muted-foreground">
                          No items found
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Bill Summary */}
              <div>
                <h3 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
                  <Receipt className="w-4 h-4" />
                  BILL SUMMARY
                </h3>
                <Card>
                  <CardContent className="pt-6 space-y-2.5">
                    <SummaryRow
                      label="Subtotal"
                      value={selectedInvoice.subtotal}
                      bold
                    />
                    <SummaryRow
                      label="Discount"
                      value={selectedInvoice.discount_amount}
                      negative
                    />
                    <SummaryRow
                      label="Tax (GST)"
                      value={selectedInvoice.tax_amount}
                    />
                    <SummaryRow
                      label="Delivery Charge"
                      value={selectedInvoice.delivery_charge || 0}
                    />
                    <SummaryRow
                      label="Service Charge"
                      value={selectedInvoice.service_charge || 0}
                    />
                    <Separator className="my-3" />
                    <SummaryRow
                      label="Grand Total"
                      value={selectedInvoice.total_amount}
                      highlight
                    />
                  </CardContent>
                </Card>
              </div>

              {/* Payment Details */}
              <div>
                <h3 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
                  <CreditCard className="w-4 h-4" />
                  PAYMENT DETAILS
                </h3>
                <Card>
                  <CardContent className="pt-6 space-y-2.5">
                    <DetailRow
                      label="Payment Method"
                      value={selectedInvoice.payment_method || "N/A"}
                    />
                    <DetailRow
                      label="Payment Status"
                      value={selectedInvoice.payment_status || "Pending"}
                    />
                    <DetailRow
                      label="Amount Paid"
                      value={`₹${parseFloat(
                        selectedInvoice.amount_paid || 0,
                      ).toFixed(2)}`}
                    />
                    <DetailRow
                      label="Balance Due"
                      value={`₹${parseFloat(
                        selectedInvoice.balance_due || 0,
                      ).toFixed(2)}`}
                    />
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </MainLayout>
  );
}

// Helper component for summary rows
function SummaryRow({
  label,
  value,
  bold = false,
  highlight = false,
  negative = false,
}) {
  const formattedValue = typeof value === "number" ? value.toFixed(2) : value;

  return (
    <div
      className={cn(
        "flex justify-between items-center py-1",
        highlight && "bg-primary/5 px-3 -mx-3 rounded-lg py-2",
      )}
    >
      <span
        className={cn(
          "text-sm",
          bold && "font-semibold",
          highlight && "font-semibold text-foreground",
          !highlight && "text-muted-foreground",
        )}
      >
        {label}
      </span>
      <span
        className={cn(
          "font-medium",
          bold && "font-semibold",
          highlight && "text-lg font-bold text-primary",
          negative && "text-destructive",
        )}
      >
        {negative && value > 0 ? "-" : ""}₹{formattedValue}
      </span>
    </div>
  );
}

// Helper component for detail rows
function DetailRow({ label, value }) {
  return (
    <div className="flex justify-between items-center py-1">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="font-medium text-sm">{value}</span>
    </div>
  );
}
